// @flow

import assign from 'lodash/assign';
import pick from 'lodash/pick';
import {browserHistory} from 'react-router';

import {
  dataChangesPublisher,
  procedureDetailSource,
  procedureVersionSource,
  userDataSource,
} from 'app/demo/demo_fixtures/';
import {nowISOString, waitAndReturn} from 'app/demo/utils';

import {
  ListResponseType,
  ProcedureVersionSerializedType,
  RawProcedureHashidSerializedType,
} from 'sweet-types';

let user = userDataSource;
let procedure = procedureDetailSource;
let version = procedureVersionSource;

/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/ban-types */

export const procedureDetailByHashidFetcher = (
  hashid: string
): Promise<typeof procedure> => {
  return waitAndReturn(procedure);
};

export const procedureListFetcher = (args: {}): Promise<
  ListResponseType<RawProcedureHashidSerializedType>
> =>
  waitAndReturn({
    count: 0,
    next: null,
    previous: null,
    results: [],
    num_pages: 1,
  });

export const procedureDeleter = (url: string): Promise<unknown> =>
  waitAndReturn(() => {
    browserHistory.replace(procedure.html_url);
    // @ts-expect-error only firefox supports boolean param
    window.location.reload(true);
  });

export const procedureDuplicator = (url: string): Promise<typeof procedure> => {
  return waitAndReturn(procedure);
};

export const procedurePatcher = (
  url: string,
  data: Partial<RawProcedureHashidSerializedType>
): Promise<typeof procedure> => {
  return waitAndReturn(procedure);
};

export const procedureVersionPatcher = (
  url: string,
  data: {
    description?: string;
    content: Array<any>;
  }
): Promise<ProcedureVersionSerializedType> => {
  data.content = data.content.filter((step) => step.delete == null);

  assign(version, {
    ...data,
    content: data.content != null ? JSON.stringify(data.content) : undefined,
    description:
      data.description != null ? JSON.stringify(data.description) : undefined,
    modified_at: nowISOString(),
    editor: pick(user, ['id', 'is_deleted', 'url', 'name', 'email']),
  });

  if (
    procedure.current_version.id === version.id &&
    procedure.approved_at != null
  ) {
    // handle person patching something that was approved already.
    assign(version, {
      id: version.id + 1,
      approved_at: null,
      created_at: nowISOString(),
      is_current: false,
      url: version.html_url.replace(/\d+\/$/, version.id + 1 + '/'),
      html_url: version.html_url.replace(/\d+\/$/, version.id + 1 + '/'),
    });
  }
  return waitAndReturn(version);
};

export const procedureVersionsFetcher = (params: {}): Promise<
  ListResponseType<typeof version>
> => {
  return waitAndReturn({
    count: 1,
    next: null,
    previous: null,
    results: [version],
    num_pages: 1,
  });
};

export const procedureVersionDetailFetcher = (
  versionId: number
): Promise<typeof version> => waitAndReturn(version);

export const procedureVersionDeleter = (
  url: string
): Promise<typeof version> => {
  return waitAndReturn(version);
};

export const procedureVersionApprover = (
  versionURL: string,
  notify: boolean
): Promise<typeof version> => {
  assign(version, {
    modified_at: nowISOString(),
    approved_at: nowISOString(),
    is_current: true,
  });

  assign(procedure, {
    approved_by: pick(user, ['id', 'is_deleted', 'url', 'name', 'email']),
    approved_at: nowISOString(),
    modified_at: nowISOString(),
    edited_at: nowISOString(),
    name: version.name,
    current_version: pick(version, ['id', 'url', 'html_url']),
  });

  dataChangesPublisher.publish('procedure', procedure);
  return waitAndReturn(version);
};

export const procedureVersionApprovalRequester = (
  versionURL: string,
  message: string
): Promise<typeof version> => {
  return waitAndReturn(version);
};

export const procedureExporter = (
  content_type: string,
  object_id: number
): Promise<typeof procedure> => {
  return waitAndReturn(procedure);
};

export const procedureCreator = (data?: {
  name?: string;
}): Promise<typeof procedure> => {
  return waitAndReturn(procedure);
};

/* eslint-enable */
